import React, { useState } from "react";
import PageHero from "../components/PageHero";
import { useToast } from "../hooks/use-toast";
import { CheckCircle2, Loader2 } from "lucide-react";
import api from "../lib/api";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";

export default function AdmissionsPage() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    qualification: "",
    course_interest: "",
    city: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/admissions", form);
      setDone(true);
      toast({ title: "Application submitted!", description: "Our team will contact you soon." });
      setForm({ full_name: "", email: "", phone: "", qualification: "", course_interest: "", city: "", message: "" });
    } catch (err) {
      toast({ title: "Submission failed", description: err?.response?.data?.detail || "Try again later", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHero path="admissions.hero" kicker="Admissions Open" title="Apply for admission" subtitle="Fill in your details to learn more about our technical training courses. Call 8800288994 for assistance or to enquire about our 3½-year technical courses." />
      <section className="py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
          <div className="lg:col-span-2">
            <h2 className="text-2xl font-bold text-[#0B2A6B] mb-4">Why apply to Kiki?</h2>
            <ul className="space-y-4">
              {[
                "3.5 years German Technical Training Course",
                "For 10th/12th pass students (all streams)",
                "Stipend from Year 2 onwards",
                "German HWK Koblenz Certification",
                "95%+ placement track record",
                "World-class faculty and facilities",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-yellow-500 mt-0.5" />
                  <span className="text-slate-700">{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-3">
            {done ? (
              <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-10 text-center">
                <CheckCircle2 className="h-14 w-14 text-yellow-500 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-[#0B2A6B] mb-2">Application Received!</h3>
                <p className="text-slate-600 mb-6">Our admissions team will contact you within 24-48 hours.</p>
                <Button onClick={() => setDone(false)} className="bg-[#0B2A6B] hover:bg-[#061a47] text-white">Submit Another</Button>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl shadow-sm border border-slate-100 p-7 space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Full Name *"><Input required value={form.full_name} onChange={(e) => update("full_name", e.target.value)} placeholder="Your full name" /></Field>
                  <Field label="Email *"><Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} placeholder="you@email.com" /></Field>
                  <Field label="Phone *"><Input required value={form.phone} onChange={(e) => update("phone", e.target.value)} placeholder="+91..." /></Field>
                  <Field label="City"><Input value={form.city} onChange={(e) => update("city", e.target.value)} placeholder="Your city" /></Field>
                  <Field label="Qualification *">
                    <Select value={form.qualification} onValueChange={(v) => update("qualification", v)} required>
                      <SelectTrigger><SelectValue placeholder="Select qualification" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Degree Holder / Currently studying">Degree Holder / Currently studying</SelectItem>
                        <SelectItem value="Diploma Passed / Currently studying">Diploma Passed / Currently studying</SelectItem>
                        <SelectItem value="ITI Passed / Currently studying">ITI Passed / Currently studying</SelectItem>
                        <SelectItem value="12 Pass">12 Pass</SelectItem>
                        <SelectItem value="11 Pass">11 Pass</SelectItem>
                        <SelectItem value="10 Pass">10 Pass</SelectItem>
                        <SelectItem value="Other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Course Interested In *">
                    <Select value={form.course_interest} onValueChange={(v) => update("course_interest", v)} required>
                      <SelectTrigger><SelectValue placeholder="Select course" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Tool & Die Making">Tool &amp; Die Making</SelectItem>
                        <SelectItem value="Precision Machinist">Precision Machinist</SelectItem>
                        <SelectItem value="Mechatronics">Mechatronics</SelectItem>
                        <SelectItem value="Diploma in Tool Design">Diploma in Tool Design</SelectItem>
                        <SelectItem value="Post Diploma in Automation">Post Diploma in Automation</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
                <Field label="Your Message"><Textarea rows={4} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="Tell us about yourself..." /></Field>
                <Button type="submit" disabled={loading} className="w-full bg-yellow-400 hover:bg-yellow-500 text-[#0B2A6B] font-bold h-12 text-base">
                  {loading ? <><Loader2 className="h-4 w-4 animate-spin mr-2" />Submitting...</> : "Submit Application"}
                </Button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-sm font-semibold text-slate-700 mb-1.5 inline-block">{label}</Label>
      {children}
    </div>
  );
}
